import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/legacy-ui';
import { cn } from '@/utils/classname';

export interface MSRPCalculatorDisplayProps {
  msrp: number;
  floorPrice: number;
  breakdown: {
    fqdnCost: number;
    assetCost: number;
  };
  floorBreakdown: {
    fqdnCost: number;
    assetCost: number;
  };
  className?: string;
  title?: string;
  tooltipText?: string;
}

/**
 * MSRPCalculatorDisplay - Reusable component for displaying MSRP calculation with breakdown
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export function MSRPCalculatorDisplay({
  msrp,
  floorPrice,
  breakdown,
  floorBreakdown,
  className,
  title = 'MSRP Price',
  tooltipText = 'Auto-Calculated Based on Coverage Limits',
}: MSRPCalculatorDisplayProps) {
  return (
    <section className={cn('space-y-4', className)}>
      <h3 className="text-chariot-text-primary text-lg font-semibold">
        {title}
      </h3>
      <Container className="bg-chariot-background-primary">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-chariot-text-primary mb-2 flex items-center gap-2 text-sm font-medium">
              {title}
              {tooltipText && (
                <Tooltip title={tooltipText}>
                  <InformationCircleIcon className="text-chariot-text-secondary h-4 w-4" />
                </Tooltip>
              )}
            </p>
            {breakdown && (
              <div className="text-chariot-text-secondary mt-3 text-xs">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <div className="font-medium">Floor</div>
                  <div className="font-medium">MSRP</div>
                  <div>
                    Application Scanning: $
                    {floorBreakdown.fqdnCost.toLocaleString()}
                  </div>
                  <div>
                    Application Scanning: ${breakdown.fqdnCost.toLocaleString()}
                  </div>
                  <div>
                    Asset Coverage: ${floorBreakdown.assetCost.toLocaleString()}
                  </div>
                  <div>
                    Asset Coverage: ${breakdown.assetCost.toLocaleString()}
                  </div>
                  <div className="border-chariot-stroke border-t pt-1 font-medium">
                    Total: ${floorPrice.toLocaleString()}
                  </div>
                  <div className="border-chariot-stroke border-t pt-1 font-medium">
                    Total: ${msrp.toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="text-chariot-text-primary text-right">
            <div className="text-2xl font-bold">${msrp.toLocaleString()}</div>
            <div className="text-chariot-text-secondary text-xs">
              Floor: ${floorPrice.toLocaleString()}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
